import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { GridDivider } from '@sharedComponents/Separators';
import { GeneralTextService, ServiceItem } from '@sharedComponents/Service';
import { ReactFCV } from '@types';
import { DIVIDER_COLOR, DIVIDER_HEIGHT } from '@utils/constants';
import { arrayEquals } from '@utils/general';
import { CypressTestNamespace } from '@utils/test';

import { IToggleEcoPackagingAction } from '../../../context';
import { IDiscoveryBox, IEcoPackaging, IGiftPackaging } from '../../../model';
import { messages } from '../messages';
import { EcoPackagingState } from '../model';
import { getEcoPackagingModel } from '../utils';

import { EcoPackagingDescription } from './EcoPackagingDescription';

export interface IEcoPackagingServiceProps {
  ecoPackaging?: IEcoPackaging | null;
  ecoPackagingPrice: number;
  giftPackagingBoxes: IGiftPackaging[];
  toggleEcoPackaging: (data: IToggleEcoPackagingAction) => void;
  discoveryBoxes: IDiscoveryBox[];
  ecoPackagingOn: boolean;
  isDualPriceLayout: boolean;
}

const arePropertiesEqual = (prevProps: IEcoPackagingServiceProps, nextProps: IEcoPackagingServiceProps): boolean =>
  prevProps.ecoPackaging === nextProps.ecoPackaging &&
  prevProps.ecoPackagingPrice === nextProps.ecoPackagingPrice &&
  arrayEquals(prevProps.giftPackagingBoxes, nextProps.giftPackagingBoxes) &&
  arrayEquals(prevProps.discoveryBoxes, nextProps.discoveryBoxes) &&
  prevProps.toggleEcoPackaging === nextProps.toggleEcoPackaging &&
  prevProps.isDualPriceLayout === nextProps.isDualPriceLayout;

export const EcoPackagingService: ReactFCV<IEcoPackagingServiceProps> = React.memo(
  ({
    ecoPackaging,
    ecoPackagingPrice,
    giftPackagingBoxes,
    toggleEcoPackaging,
    discoveryBoxes,
    ecoPackagingOn,
    isDualPriceLayout,
  }) => {
    const { formatMessage } = useIntl();

    const model = getEcoPackagingModel(
      ecoPackagingOn,
      giftPackagingBoxes,
      ecoPackagingPrice,
      discoveryBoxes,
      ecoPackaging,
    );

    const onChangeEcoPackaging = useCallback(() => {
      toggleEcoPackaging({
        service: ecoPackaging,
        active: !ecoPackaging,
      });
    }, [toggleEcoPackaging, ecoPackaging]);

    return (
      <>
        <GridDivider color={DIVIDER_COLOR} height={DIVIDER_HEIGHT} />
        <ServiceItem
          data-testid="eco-packaging-service"
          price={model.price}
          isDualPriceLayout={isDualPriceLayout}
          title={
            <GeneralTextService
              title={formatMessage(messages.ecoWrappingTitle)}
              infoMarkText={formatMessage(messages.serviceLegalInfo)}
            />
          }
          infoText={<EcoPackagingDescription state={model.state} />}
          serviceActive={model.checked}
          serviceDisabled={model.state !== EcoPackagingState.ENABLED && !model.checked}
          freePriceText={formatMessage(messages.forFreePriceText)}
          onClick={onChangeEcoPackaging}
          dataTestId={CypressTestNamespace.ECartPageServices.ECO_PACKAGING_SERVICE}
        />
      </>
    );
  },
  arePropertiesEqual,
);
